import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { AlertTriangle, CheckCircle2, Clock, Info, RotateCcw, XCircle } from 'lucide-react';
import ordersApi from 'api/requests/orders';
import ResponsiveModal from 'components/_UI/ResponsiveModal';
import colors from 'theme/colors';

// Os motivos que a API aceita (OrderRefundRequest::REASONS), na ordem da lista do app.
const REASONS = [
    { key: 'cant_attend', label: 'Não vou conseguir ir' },
    { key: 'event_changed', label: 'O evento mudou (data, local, atração)' },
    { key: 'bought_by_mistake', label: 'Comprei errado' },
    { key: 'duplicate', label: 'Comprei duas vezes' },
    { key: 'other', label: 'Outro motivo' },
];

const DETAILS_MAX_LENGTH = 1000;

const FALLBACK_ERROR = 'Não foi possível enviar o pedido agora. Tente novamente em instantes.';

// Compras que não são de ingresso de evento (ou que já não têm o que estornar)
// não ganham seção: o status do pedido já conta a história.
const HIDDEN_CODES = ['not_event_order', 'free', 'not_paid', 'already_refunded'];

const WINDOW_CODES = ['purchase_window', 'event_start'];

/**
 * Pedido de estorno do comprador, no fim do detalhe da carteira.
 *
 * Quem decide se dá para pedir é a API (`GET /orders/{id}/refund-request`); aqui
 * só se mostra o resultado. O estorno é por compra, e a carteira agrupa por
 * evento — um cartão pode juntar mais de uma compra, então cada uma aparece com
 * a própria situação (quase sempre é uma só).
 *
 * - Dentro do prazo do direito do consumidor: botão ativo.
 * - Fora do prazo: botão desabilitado e aviso em vermelho mandando falar com o
 *   produtor, que ainda pode estornar por conta própria.
 * - Pedido feito: "em análise" até o produtor responder; se ele recusar, o
 *   motivo aparece aqui.
 *
 * Depois que o evento terminou não há mais o que pedir, então o aviso do prazo
 * (e o botão) saem: só ficam as compras que já têm um pedido, com o resultado.
 */
export default function WalletRefund({ orderIds, eventName, eventEnded = false }) {
    const [statuses, setStatuses] = useState({});
    const [requestingOrderId, setRequestingOrderId] = useState(null);

    const key = orderIds.join(',');

    useEffect(() => {
        let active = true;

        orderIds.forEach(orderId => {
            ordersApi.refundRequestStatus(orderId)
                .then(({ data }) => active && setStatuses(current => ({ ...current, [orderId]: data })))
                .catch(() => {});
        });

        return () => { active = false; };
    }, [key]);

    function handleRequested(orderId, request) {
        setStatuses(current => ({
            ...current,
            [orderId]: { ...current[orderId], can_request: false, blocked_code: 'pending', request },
        }));
        setRequestingOrderId(null);
    }

    const visible = orderIds.filter(orderId => {
        const status = statuses[orderId];

        if (!status) return false;
        if (status.request) return true;

        return !eventEnded && !HIDDEN_CODES.includes(status.blocked_code);
    });

    if (!visible.length) return null;

    return (
        <Section aria-label='Estorno'>
            <SectionTitle>Estorno</SectionTitle>

            {visible.map(orderId => (
                <RefundRow
                    key={orderId}
                    orderId={orderId}
                    numbered={orderIds.length > 1}
                    status={statuses[orderId]}
                    onRequest={() => setRequestingOrderId(orderId)}
                />
            ))}

            <RequestModal
                orderId={requestingOrderId}
                eventName={eventName}
                onClose={() => setRequestingOrderId(null)}
                onRequested={handleRequested}
            />
        </Section>
    );
}

function RefundRow({ orderId, numbered, status, onRequest }) {
    const label = numbered ? `Compra #${orderId}` : null;
    const request = status.request;
    const noticeId = `refund-notice-${orderId}`;

    const outcome = outcomeFor(status);
    const disabled = !status.can_request;

    return (
        <Row>
            {label && <RowLabel>{label}</RowLabel>}

            {outcome && (
                <Notice id={noticeId} $tone={outcome.tone} role={outcome.tone === 'danger' ? 'alert' : 'status'}>
                    <outcome.Icon size={16} aria-hidden='true' />
                    <NoticeText>
                        <strong>{outcome.title}</strong>
                        {outcome.text && <span>{outcome.text}</span>}
                    </NoticeText>
                </Notice>
            )}

            {status.can_request && status.window?.closes_at && (
                <Hint>
                    Você pode pedir o estorno até {dayjs(status.window.closes_at).format('DD/MM/YYYY [às] HH:mm')},
                    dentro do prazo do direito do consumidor.
                </Hint>
            )}

            <RequestButton
                type='button'
                disabled={disabled}
                aria-describedby={outcome ? noticeId : undefined}
                onClick={onRequest}
            >
                <RotateCcw size={15} aria-hidden='true' />
                {request?.status === 'pending' ? 'Estorno solicitado' : 'Solicitar estorno'}
            </RequestButton>
        </Row>
    );
}

/**
 * O aviso colorido de cada situação da compra: o pedido (em análise, recusado,
 * aceito) ou o motivo de o botão estar desabilitado. `null` quando nada a dizer
 * (botão ativo, que já fala por si).
 */
function outcomeFor(status) {
    const { request, blocked_code: code } = status;

    if (request?.status === 'pending') {
        return {
            tone: 'warning',
            Icon: Clock,
            title: 'Estorno em análise',
            text: `Você pediu em ${dayjs(request.created_at).format('DD/MM/YYYY')}. O produtor vai responder e você será avisado por notificação.`,
        };
    }

    if (request?.status === 'denied') {
        return {
            tone: 'danger',
            Icon: XCircle,
            title: 'O produtor recusou o pedido de estorno',
            text: request.answer ? `Motivo: ${request.answer}` : 'Fale direto com o produtor ou com o suporte se precisar.',
        };
    }

    if (request?.status === 'approved') {
        return {
            tone: 'success',
            Icon: CheckCircle2,
            title: 'Estorno aceito',
            text: 'O valor volta pela mesma forma de pagamento da compra.',
        };
    }

    if (WINDOW_CODES.includes(code)) {
        return {
            tone: 'danger',
            Icon: AlertTriangle,
            title: 'O botão está desabilitado porque já passou o prazo de estorno segundo a lei do direito do consumidor.',
            text: `${windowDetail(code, status.window)} Se quiser tentar um estorno, entre em contato diretamente com o produtor do evento.`,
        };
    }

    if (!status.can_request && status.blocked_reason) {
        return { tone: 'neutral', Icon: Info, title: status.blocked_reason, text: null };
    }

    return null;
}

function windowDetail(code, rule = {}) {
    if (code === 'purchase_window') {
        return `O prazo é de ${rule.days ?? 7} dias após a compra.`;
    }

    return `O pedido só pode ser feito até ${rule.hours_before_event ?? 48} horas antes do evento.`;
}

function RequestModal({ orderId, eventName, onClose, onRequested }) {
    const [reason, setReason] = useState(null);
    const [details, setDetails] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);

    // Cada abertura começa do zero: o que foi digitado para uma compra não vale para outra.
    useEffect(() => {
        setReason(null);
        setDetails('');
        setError(null);
        setSending(false);
    }, [orderId]);

    async function submit() {
        if (!reason || sending) return;

        setSending(true);
        setError(null);

        try {
            const { data } = await ordersApi.requestRefund(orderId, { reason, details: details.trim() || null });
            onRequested(orderId, data);
        } catch (err) {
            setError(err?.response?.data?.message || FALLBACK_ERROR);
        } finally {
            setSending(false);
        }
    }

    return (
        <ResponsiveModal isOpen={orderId !== null} onClose={sending ? () => false : onClose} title='Solicitar estorno'>
            <ModalContent>
                <ModalSubtitle>
                    {`O pedido de ${eventName || 'esta compra'} vai para o produtor, que pode aceitar ou recusar. `}
                    A resposta chega nas suas notificações, e o valor volta pela mesma forma de pagamento.
                </ModalSubtitle>

                <Fieldset>
                    <Legend>Motivo</Legend>
                    {REASONS.map(item => (
                        <ReasonOption key={item.key} $selected={reason === item.key}>
                            <input
                                type='radio'
                                name='refund-reason'
                                value={item.key}
                                checked={reason === item.key}
                                onChange={() => setReason(item.key)}
                            />
                            <span>{item.label}</span>
                        </ReasonOption>
                    ))}
                </Fieldset>

                <FieldGroup>
                    <FieldLabel htmlFor='refund-details'>Quer contar mais? (opcional)</FieldLabel>
                    <Details
                        id='refund-details'
                        rows={3}
                        maxLength={DETAILS_MAX_LENGTH}
                        value={details}
                        onChange={event => setDetails(event.target.value)}
                    />
                </FieldGroup>

                {error && <ErrorText role='alert'>{error}</ErrorText>}

                <SubmitButton type='button' disabled={!reason || sending} onClick={submit}>
                    {sending ? 'Enviando…' : 'Enviar pedido'}
                </SubmitButton>
            </ModalContent>
        </ResponsiveModal>
    );
}

const TONES = {
    warning: { bg: '#FEF3C7', border: '#FDE68A', ink: '#92400E' },
    danger: { bg: '#FEE2E2', border: '#FECACA', ink: '#991B1B' },
    success: { bg: '#DCFCE7', border: '#BBF7D0', ink: '#166534' },
    neutral: { bg: colors.neutral, border: colors.neutralDark, ink: colors.night },
};

const Section = styled.section`
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin-bottom: 24px;
    padding: 16px;
    background: ${colors.neutralLight};
    border: 1px solid ${colors.neutralDark};
    border-radius: 14px;
`;

const SectionTitle = styled.h3`
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const Row = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;

    & + & {
        padding-top: 14px;
        border-top: 1px solid ${colors.neutralDark};
    }
`;

const RowLabel = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.night};
`;

const Notice = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 14px;
    border-radius: 10px;
    border: 1px solid ${({ $tone }) => TONES[$tone].border};
    background: ${({ $tone }) => TONES[$tone].bg};
    color: ${({ $tone }) => TONES[$tone].ink};
    font-size: 13px;
    line-height: 1.45;

    svg {
        flex-shrink: 0;
        margin-top: 2px;
    }
`;

const NoticeText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    overflow-wrap: anywhere;

    strong {
        font-weight: 600;
    }
`;

const Hint = styled.p`
    margin: 0;
    font-size: 13px;
    line-height: 1.45;
    color: ${colors.night};
`;

const RequestButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    padding: 12px;
    border-radius: 10px;
    border: 1.5px solid ${colors.occasionalPurple};
    background: transparent;
    color: ${colors.occasionalPurple};
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        background: ${colors.occasionalPurple}10;
    }

    &:active:not(:disabled) {
        transform: scale(0.98);
    }

    &:disabled {
        border-color: ${colors.nightLight};
        color: ${colors.nightLight};
        cursor: not-allowed;
    }
`;

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    text-align: left;
`;

const ModalSubtitle = styled.p`
    margin: 0 0 16px 0;
    font-size: 14px;
    line-height: 1.45;
    color: ${colors.night};
`;

const Fieldset = styled.fieldset`
    margin: 0 0 12px 0;
    padding: 0;
    border: none;
`;

const Legend = styled.legend`
    padding: 0;
    margin-bottom: 6px;
    font-size: 13px;
    font-weight: 600;
    color: ${colors.night};
`;

const ReasonOption = styled.label`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 6px;
    padding: 10px 12px;
    border-radius: 10px;
    border: 1.5px solid ${({ $selected }) => ($selected ? colors.occasionalPurple : colors.neutralDark)};
    background: ${({ $selected }) => ($selected ? `${colors.occasionalPurple}0D` : colors.neutralLight)};
    font-size: 14px;
    color: ${colors.nightDark};
    cursor: pointer;

    input {
        accent-color: ${colors.occasionalPurple};
    }
`;

const FieldGroup = styled.div`
    width: 100%;
    margin-bottom: 12px;
`;

const FieldLabel = styled.label`
    display: block;
    margin-bottom: 6px;
    font-size: 13px;
    font-weight: 600;
    color: ${colors.night};
`;

const Details = styled.textarea`
    width: 100%;
    box-sizing: border-box;
    padding: 12px 14px;
    border-radius: 10px;
    border: 1.5px solid ${colors.neutralDark};
    background: ${colors.neutralLight};
    font-family: inherit;
    font-size: 14px;
    color: ${colors.nightDark};
    resize: vertical;
    outline: none;
    transition: border-color 0.2s ease;

    &:focus {
        border-color: ${colors.occasionalPurple};
    }
`;

const ErrorText = styled.p`
    margin: 0 0 8px 0;
    font-size: 13px;
    text-align: center;
    color: ${colors.danger};
`;

const SubmitButton = styled.button`
    width: 100%;
    padding: 14px;
    border-radius: 10px;
    border: none;
    background: ${colors.occasionalPurple};
    color: ${colors.neutralLight};
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        opacity: 0.9;
        box-shadow: 0 4px 12px ${colors.occasionalPurple}40;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;
